// src/components/ui/index.js
// Reusable UI primitives — Button, Card, Badge, Input, Modal, Spinner, etc.
import React from 'react';
import styles from './ui.module.css';

// ── Button ────────────────────────────────────────────────────────────────────
export function Button({
    children,
    variant = 'primary',
    size = 'md',
    loading = false,
    icon,
    iconRight,
    fullWidth,
    className = '',
    disabled,
    ...props
}) {
    return (
        <button
            className={`${styles.btn} ${styles[`btn-${variant}`]} ${styles[`btn-${size}`]} ${fullWidth ? styles['btn-full'] : ''} ${className}`}
            disabled={disabled || loading}
            {...props}
        >
            {loading && <span className={styles.spinner} />}
            {!loading && icon && <span className={styles.btnIcon}>{icon}</span>}
            <span>{children}</span>
            {!loading && iconRight && <span className={styles.btnIcon}>{iconRight}</span>}
        </button>
    );
}

// ── Card ──────────────────────────────────────────────────────────────────────
export function Card({ children, className = '', padding = true, hover = false, ...props }) {
    return (
        <div
            className={`${styles.card} ${padding ? styles['card-padded'] : ''} ${hover ? styles['card-hover'] : ''} ${className}`}
            {...props}
        >
            {children}
        </div>
    );
}

// ── Badge ─────────────────────────────────────────────────────────────────────
export function Badge({ children, variant = 'neutral', size = 'sm' }) {
    return (
        <span className={`${styles.badge} ${styles[`badge-${variant}`]} ${styles[`badge-${size}`]}`}>
            {children}
        </span>
    );
}

// ── Input ─────────────────────────────────────────────────────────────────────
export function Input({ label, error, hint, icon, className = '', ...props }) {
    return (
        <div className={styles.inputWrapper}>
            {label && <label className={styles.label}>{label}</label>}
            <div className={styles.inputInner}>
                {icon && <span className={styles.inputIcon}>{icon}</span>}
                <input
                    className={`${styles.input} ${icon ? styles['input-with-icon'] : ''} ${error ? styles['input-error'] : ''} ${className}`}
                    {...props}
                />
            </div>
            {error && <span className={styles.inputError}>{error}</span>}
            {hint && !error && <span className={styles.inputHint}>{hint}</span>}
        </div>
    );
}

// ── Select ────────────────────────────────────────────────────────────────────
export function Select({ label, error, children, className = '', ...props }) {
    return (
        <div className={styles.inputWrapper}>
            {label && <label className={styles.label}>{label}</label>}
            <select className={`${styles.select} ${error ? styles['input-error'] : ''} ${className}`} {...props}>
                {children}
            </select>
            {error && <span className={styles.inputError}>{error}</span>}
        </div>
    );
}

// ── Textarea ──────────────────────────────────────────────────────────────────
export function Textarea({ label, error, hint, className = '', ...props }) {
    return (
        <div className={styles.inputWrapper}>
            {label && <label className={styles.label}>{label}</label>}
            <textarea
                className={`${styles.textarea} ${error ? styles['input-error'] : ''} ${className}`}
                {...props}
            />
            {error && <span className={styles.inputError}>{error}</span>}
            {hint && !error && <span className={styles.inputHint}>{hint}</span>}
        </div>
    );
}

// ── Spinner ───────────────────────────────────────────────────────────────────
export function Spinner({ size = 'md' }) {
    return <span className={`${styles.spinner} ${styles[`spinner-${size}`]}`} />;
}

// ── Empty state ───────────────────────────────────────────────────────────────
export function EmptyState({ icon, title, description, action }) {
    return (
        <div className={styles.emptyState}>
            {icon && <div className={styles.emptyIcon}>{icon}</div>}
            <h3 className={styles.emptyTitle}>{title}</h3>
            {description && <p className={styles.emptyDesc}>{description}</p>}
            {action && <div className={styles.emptyAction}>{action}</div>}
        </div>
    );
}

// ── Modal ─────────────────────────────────────────────────────────────────────
export function Modal({ open, onClose, title, children, size = 'md' }) {
    if (!open) return null;
    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div
                className={`${styles.modal} ${styles[`modal-${size}`]}`}
                onClick={(e) => e.stopPropagation()}
            >
                <div className={styles.modalHeader}>
                    <h3 className={styles.modalTitle}>{title}</h3>
                    <button className={styles.modalClose} onClick={onClose}>✕</button>
                </div>
                <div className={styles.modalBody}>{children}</div>
            </div>
        </div>
    );
}

// ── Stat card ─────────────────────────────────────────────────────────────────
export function StatCard({ label, value, sub, icon, trend, accent = false }) {
    return (
        <Card className={`${styles.statCard} ${accent ? styles['statCard-accent'] : ''}`}>
            <div className={styles.statTop}>
                <span className={styles.statLabel}>{label}</span>
                {icon && <span className={styles.statIcon}>{icon}</span>}
            </div>
            <div className={styles.statValue}>{value ?? '—'}</div>
            {sub && <div className={styles.statSub}>{sub}</div>}
            {trend && (
                <div className={`${styles.statTrend} ${trend > 0 ? styles['trend-up'] : styles['trend-down']}`}>
                    {trend > 0 ? '↑' : '↓'} {Math.abs(trend)}%
                </div>
            )}
        </Card>
    );
}

// ── Section header ────────────────────────────────────────────────────────────
export function SectionHeader({ title, description, actions }) {
    return (
        <div className={styles.sectionHeader}>
            <div>
                <h2 className={styles.sectionTitle}>{title}</h2>
                {description && <p className={styles.sectionDesc}>{description}</p>}
            </div>
            {actions && <div className={styles.sectionActions}>{actions}</div>}
        </div>
    );
}

// ── Table ─────────────────────────────────────────────────────────────────────
export function Table({ columns, data, loading, emptyMessage = 'No Data' }) {
    if (loading) {
        return (
            <div className={styles.tableWrap}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            {columns.map((c) => <th key={c.key}>{c.label}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {[...Array(5)].map((_, i) => (
                            <tr key={i}>
                                {columns.map((c) => (
                                    <td key={c.key}><div className={`skeleton ${styles.skeletonCell}`} /></td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    if (!data || data.length === 0) {
        return (
            <div className={styles.tableWrap}>
                <div className={styles.tableEmpty}>{emptyMessage}</div>
            </div>
        );
    }

    return (
        <div className={styles.tableWrap}>
            <table className={styles.table}>
                <thead>
                    <tr>
                        {columns.map((c) => <th key={c.key} style={c.width ? { width: c.width } : {}}>{c.label}</th>)}
                    </tr>
                </thead>
                <tbody>
                    {data.map((row, i) => (
                        <tr key={row.id || i}>
                            {columns.map((c) => (
                                <td key={c.key}>
                                    {c.render ? c.render(row[c.key], row) : row[c.key] ?? '—'}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// ── Confirm Modal ─────────────────────────────────────────────────────────────
export function ConfirmModal({ open, onClose, onConfirm, title, message, confirmLabel = 'Delete', confirmVariant = 'danger', loading = false }) {
    return (
        <Modal open={open} onClose={onClose} title={title || 'Confirm'} size="sm">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', lineHeight: 1.6, margin: 0 }}>
                    {message}
                </p>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    <Button variant="secondary" onClick={onClose} disabled={loading}>Cancel</Button>
                    <Button variant={confirmVariant} loading={loading} onClick={onConfirm}>{confirmLabel}</Button>
                </div>
            </div>
        </Modal>
    );
}
